import { useCallback, useEffect, useState } from "react";

import { api, post } from "./api";
import type {
  TeslaAndroidSettingName,
  TeslaAndroidSettings,
  TeslaAndroidSettingsResponse,
} from "./types";


const settingDefinitions: Array<{
  setting: TeslaAndroidSettingName;
  state: keyof Pick<TeslaAndroidSettings,
    "offlineModeEnabled" | "telemetryBlockingEnabled" | "firmwareDownloadBlockingEnabled">;
  label: string;
  description: string;
}> = [
  {
    setting: "offlineMode",
    state: "offlineModeEnabled",
    label: "Offline mode",
    description: "Keeps the car connected to TeslaAndroid when upstream Internet is unavailable.",
  },
  {
    setting: "telemetryBlocking",
    state: "telemetryBlockingEnabled",
    label: "Block Tesla telemetry",
    description: "Reduces vehicle telemetry traffic. Disable temporarily for the Kingshot network test.",
  },
  {
    setting: "firmwareDownloadBlocking",
    state: "firmwareDownloadBlockingEnabled",
    label: "Block Tesla software downloads",
    description: "Prevents the car from downloading software updates through TeslaAndroid Wi-Fi.",
  },
];


export function TeslaAndroidSettingsDialog({
  onClose,
  onNotice,
}: {
  onClose: () => void;
  onNotice: (message: string | undefined) => void;
}) {
  const [settings, setSettings] = useState<TeslaAndroidSettings>();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<TeslaAndroidSettingName>();
  const [error, setError] = useState<string>();

  const load = useCallback(async () => {
    setLoading(true);
    setError(undefined);
    try {
      const result = await api<TeslaAndroidSettingsResponse>("/api/tesla-android/settings");
      setSettings(result.settings);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !busy) onClose();
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [busy, onClose]);

  const update = async (
    setting: TeslaAndroidSettingName,
    state: keyof Pick<TeslaAndroidSettings,
      "offlineModeEnabled" | "telemetryBlockingEnabled" | "firmwareDownloadBlockingEnabled">,
    label: string,
  ) => {
    if (!settings) return;
    const enabled = !settings[state];
    const action = enabled ? "Enable" : "Disable";
    const warning = setting === "offlineMode"
      ? "This can affect whether the car remains connected to TeslaAndroid without Internet."
      : "This changes a device-wide network policy and can affect a connected car.";
    if (!window.confirm(`${action} “${label}”?\n\n${warning}\n\nThe setting is persistent and a TeslaAndroid restart may be required.`)) return;

    setBusy(setting);
    setError(undefined);
    try {
      const result = await post<TeslaAndroidSettingsResponse>(
        "/api/tesla-android/settings",
        { setting, enabled, confirm: true },
      );
      setSettings(result.settings);
      onNotice(`${label} ${enabled ? "enabled" : "disabled"}. TeslaAndroid may require a restart before network routing changes.`);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setBusy(undefined);
    }
  };

  return <div
    className="tesla-settings-backdrop"
    onMouseDown={(event) => { if (event.currentTarget === event.target && !busy) onClose(); }}
  >
    <section
      aria-labelledby="tesla-settings-title"
      aria-modal="true"
      className="tesla-settings-dialog"
      role="dialog"
    >
      <header>
        <div>
          <span className="eyebrow">Remote device configuration</span>
          <h2 id="tesla-settings-title">TeslaAndroid Settings</h2>
        </div>
        <button aria-label="Close TeslaAndroid settings" disabled={Boolean(busy)} onClick={onClose}>×</button>
      </header>

      <div className="tesla-settings-service">
        <span className={`dot ${settings?.available ? "good" : error ? "bad" : "warn"}`} />
        <div><strong>{settings?.manager || "TeslaAndroid configuration manager"}</strong><small>Private device API bridged through authenticated IS4F</small></div>
        <button disabled={loading || Boolean(busy)} onClick={() => void load()}>Refresh</button>
      </div>

      {loading && !settings && <div className="tesla-settings-loading"><span />Reading device settings…</div>}
      {error && <div className="tesla-settings-error"><strong>Settings unavailable</strong><span>{error}</span></div>}

      {settings && <div className="tesla-setting-list">
        {settingDefinitions.map((definition) => {
          const enabled = settings[definition.state];
          return <div className="tesla-setting-row" key={definition.setting}>
            <div><strong>{definition.label}</strong><p>{definition.description}</p></div>
            <button
              aria-checked={enabled}
              aria-label={definition.label}
              className="tesla-setting-switch"
              disabled={Boolean(busy)}
              onClick={() => void update(
                definition.setting, definition.state, definition.label,
              )}
              role="switch"
            ><span />{busy === definition.setting && <em>Saving</em>}</button>
          </div>;
        })}
      </div>}

      <footer>
        <span>ⓘ</span>
        <p>These controls affect TeslaAndroid’s external network policy, not ordinary Android app settings. Changes are verified after saving but may take effect after the next TeslaAndroid startup.</p>
      </footer>
    </section>
  </div>;
}
